import { useCallback } from 'react';
import { billingService } from '@/services';
import type { StatementExportFormat } from '@/types';
import { useBackgroundExport } from './use-background-export';

const EXTENSION: Record<StatementExportFormat, string> = {
  csv: 'csv',
  xlsx: 'xlsx',
  pdf: 'pdf',
};

/**
 * Download a generated billing statement as a file (LR-509 / T6).
 *
 * Thin wrapper over `useBackgroundExport` (LR-728): the statement is exported
 * by id in the background, then saved with the server's `Content-Disposition`
 * filename (falling back to a client-side name). `exportJob` is the running
 * export, for the download menu to show. Which statement it is of needs no
 * saying: the document route remounts per statementId, so an export never
 * outlives the document it was started from.
 *
 * `format` is parameterised so the Excel/PDF buttons drop in once the backend
 * serves those formats (T2 xlsx re-scope / T3 PDF).
 */
export const useDownloadStatement = () => {
  const { start, isExporting, job, exportError } = useBackgroundExport<number>();

  const download = useCallback(
    (id: number, format: StatementExportFormat = 'csv') =>
      start(
        () => billingService.startStatementExport(id, format),
        `billing_statement_${id}.${EXTENSION[format]}`,
        id,
      ),
    [start],
  );

  return {
    download,
    isDownloading: isExporting,
    exportJob: job,
    downloadError: exportError,
  };
};
